import { isLinkedInUrl } from "@/lib/utils";
import { Prisma, ProductV2 } from "@prisma/client";
import { z } from "zod";
import { TTag } from "./tags";

export type TGetProductsRange = "today" | "weekly" | "past";

export const ReviewerDesignationSchema = z.object({
	jobTitle: z.string().optional(),
	companyName: z.string().optional(),
	companyWebsite: z.string().optional(),
	companyLogo: z.string().optional(),
	linkedin: z
		.string()
		.url({ message: "Please enter a valid LinkedIn URL" })
		.refine((val) => !val || isLinkedInUrl(val), {
			message: "Please enter a valid LinkedIn URL",
		})
		.optional(),
	isVerified: z.boolean().optional(),
	isFounder: z.boolean().optional(),
});

export const AdditionalQuestionsSchema = z.object({
	useCase: z.string().optional(),
	favouriteAspect: z.string().optional(),
	improvements: z.string().optional(),
});

export const ProductReviewFormSchema = z
	.object({
		supportRating: z.number().min(1, "Support rating must be at least 1"),
		functionalityRating: z.number().min(1, "Functionality rating must be at least 1"),
		valueRating: z.number().min(1, "Value rating must be at least 1"),
		overallRating: z.number().min(1, "Overall rating must be at least 1"),
		title: z.string().min(1, "Title is required"),
		summary: z.string().min(10, "Summary must be at least 10 characters"),
		productId: z.string(),
		usageFrequency: z.string(),
		startDate: z.preprocess(
			(val) => (val ? new Date(val as string).toISOString() : undefined),
			z.string().optional()
		),
		endDate: z.preprocess(
			(val) => (val ? new Date(val as string).toISOString() : undefined),
			z.string().optional()
		),
		additionalQuestions: AdditionalQuestionsSchema.optional(),
		reviewAs: z.string(),
		reviewerDesignation: ReviewerDesignationSchema.optional(),
		createdByUser: z
			.object({
				id: z.string(),
				name: z.string(),
				image: z.string().nullable().optional(),
				UserProfile: z
					.object({
						jobTitle: z.string().nullable().optional(),
						isVerified: z.boolean().optional(),
						isFounder: z.boolean().optional(),
						company: z.string().nullable().optional(),
					})
					.optional(),
			})
			.optional(),
		createdAt: z.string().optional(),
	})
	.refine(
		(data) => {
			if (data.startDate && data.endDate) {
				const start = new Date(data.startDate);
				const end = new Date(data.endDate);
				return !isNaN(start.getTime()) && !isNaN(end.getTime()) && end > start;
			}
			return true;
		},
		{
			message: "End Date must be after Start Date",
			path: ["endDate"],
		}
	);

export type TReviewerDesignation = z.infer<typeof ReviewerDesignationSchema>;
export type TAdditionalQuestions = z.infer<typeof AdditionalQuestionsSchema>;
export type TProductReviewForm = z.infer<typeof ProductReviewFormSchema>;

export type TProductReview = {
	id: string;
	title: string;
	summary: string;
	startDate?: string | null;
	endDate?: string | null;
	createdAt?: string;
	usageFrequency: string;
	reviewAs: string;
	valueRating: number;
	supportRating: number;
	functionalityRating: number;
	name: string;
	profilePicture: string;
	isVerified?: boolean;
	isFounder?: boolean;
	jobTitle: string;
	companyName: string | null;
	companyLogo: string | null;
	overallRating: number;
	reviewerDesignation?: TReviewerDesignation;
	additionalQuestions?: TAdditionalQuestions;
	user: {
		id: string;
		name: string;
		image?: string | null;
		UserProfile?: {
			username?: string | null;
			jobTitle?: string | null;
			isVerified?: boolean;
			isFounder?: boolean;
			company?: string | null;
		};
	};
	isVisible: boolean;
	isFeatured: boolean;
};

export type TProductMedia = {
	id: string;
	objectPath: string;
	type: "IMAGE" | "VIDEO";
	alt: string;
};

export type TProductCommentReply = {
	id: string;
	slug: string;
	content: string;
	legacyContent: string;
	author: {
		name: string;
		image: string | null;
		username: string | null;
		isVerified: boolean;
		isFounder: boolean;
		jobTitle: string | null;
		company: string | null;
	};
	createdAt: Date;
	updatedAt: Date;
	media: TProductMedia[];
	likesCount: number;
	isLikedByUser: boolean;
};

export type TProductComment = TProductCommentReply & {
	replies?: TProductCommentReply[];
};

// TODO (zeekhuge): We should definitiely have a minimal version of a large type, but we need to figure out a better name than using `Min` in it.

export type TProduct = {
	id: string;
	slug: string;
	logo: string | null;
	name: string;
	companyName: string;
	hqLocation: string | undefined;
	tagline: string;
	description: string;
	tags: { name: string; type: string }[];
	industriesServed: string[];
	totalRating: number;
	noOfRatings: number;
	website: string;
	linkedIn: string;
	stageOfFundraise: string;
	demoLink: string;
	price: Prisma.JsonValue;
	regionsOfOperation: string[];
	customTags: { name: string; type: string }[];
	integrationTags: { name: string; integrationType: string }[];
	isSponsored: boolean | null;
	team: {
		name: string;
		image: string | null;
		username: string;
		isVerified: boolean;
		isFounder: boolean;
		jobTitle: string | null;
		company: string | null;
	}[];
	screenshots: string[];
	targetCustomers: string[];
	offerings: string[];
	platformsAvailable: { android: string | null; ios: string | null }[];
	features: string[];
	faqs: {
		solution: string | null;
		milestones: string | null;
		problemsSolving: string | null;
		differentitation: string | null;
	} | null;
	supportRating: number;
	functionalityRating: number;
	valueRating: number;
	viewsCount: number;
	reviews: TProductReview[];
};

// This is a minimal version of the TProduct type, containing only essential fields.
export type TProductMin = Omit<
	Pick<
		TProduct,
		| "id"
		| "slug"
		| "logo"
		| "name"
		| "hqLocation"
		| "tagline"
		| "description"
		| "tags"
		| "industriesServed"
		| "totalRating"
	>,
	"hqLocation" | "tags" | "industriesServed" | "totalRating"
> &
	Partial<Pick<TProduct, "hqLocation" | "tags" | "industriesServed" | "totalRating">>;

export type TRatingData = {
	// TODO (zeekhuge): WHY ON EARTH WOULD WE MAKE TOTAL-RATING A STRING ?????????????????????????
	totalRating: string;
	noOfRatings: number;
	supportRating: string;
	functionalityRating: string;
	valueRating: string;
	ratingCounts: { [key: number]: number };
};

export type TGetDaySpotlightProducts = {
	id: string;
	slug: string;
	name: string;
	tagline: string;
	regions: string[];
	tags: {
		id: string;
		name: string;
	}[];
	logo: string | null;
	isSponsored?: boolean | null;
	companySlug: string;
	hqLocation: string;
}[];

export type TRegion = {
	id: string;
	name: string;
};

export type TGetProducts = (ProductV2 & {
	logo: string | null;
	tags: TTag[];
	votesCount: number;
	commentsCount: number;
	upvotes: { id: string }[];
	regions: {
		region: TRegion;
	}[];
	hqLocation: string;
	companySlug: string;
	isBasedOnRegion?: boolean;
})[];
